package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.parallelMap
import br.com.vvrefeicoes.portal.domain.model.Company
import br.com.vvrefeicoes.portal.domain.model.CompanyEmotion
import br.com.vvrefeicoes.portal.domain.model.CompanyEmotionResponse
import br.com.vvrefeicoes.portal.domain.model.CompanyMissingSyncResult
import br.com.vvrefeicoes.portal.domain.model.CompanyService
import br.com.vvrefeicoes.portal.domain.model.CompanyServiceDraft
import br.com.vvrefeicoes.portal.domain.model.CompanyType
import br.com.vvrefeicoes.portal.domain.model.CostCenter
import br.com.vvrefeicoes.portal.domain.model.DEFAULT_ADMIN_OTHERS_PERCENT
import br.com.vvrefeicoes.portal.domain.model.Line
import br.com.vvrefeicoes.portal.domain.model.PublicMonitor
import br.com.vvrefeicoes.portal.domain.model.PublicMonitorAnalytics
import br.com.vvrefeicoes.portal.domain.model.PublicMonitorService
import br.com.vvrefeicoes.portal.domain.model.ServiceTypeOption
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.CompanyRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteCompanyRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : CompanyRepository {
    private val loadMutex = Mutex()
    private var cachedCompanies: List<Company>? = null
    private var cachedToken: String? = null

    override suspend fun loadCompanies(): List<Company> =
        loadMutex.withLock {
            val token = authRepository.session.value?.token
            cachedCompanies?.takeIf { cachedToken == token }?.let { return it }
            val companies = fetchCompanies()
            cachedToken = token
            cachedCompanies = companies
            companies
        }

    private suspend fun fetchCompanies(): List<Company> {
        val firstPage = get<CompanyPageDto<CompanyDto>>("/companies?page=0&size=100")
        val extraPages =
            (1 until firstPage.page.totalPages).parallelMap { page ->
                get<CompanyPageDto<CompanyDto>>("/companies?page=$page&size=100").content
            }
        return (firstPage.content + extraPages.flatten()).map(CompanyDto::toDomain)
    }

    private suspend fun invalidateCompanies() {
        loadMutex.withLock {
            cachedCompanies = null
            cachedToken = null
        }
    }

    override suspend fun upsert(company: Company) {
        val request =
            CompanyRequestDto(
                nome = company.name,
                razaoSocial = company.legalName,
                logo = company.logo.blankToNull(),
                email = company.email.blankToNull(),
                login = company.login.blankToNull(),
                password = company.pendingPassword.blankToNull(),
                telcom = company.commercialPhone.blankToNull(),
                telcel = company.mobilePhone.blankToNull(),
                cnpj = company.cnpj.blankToNull(),
                qtFuncionarios = company.employeeCount,
                cep = company.cep.blankToNull(),
                rua = company.street.blankToNull(),
                numero = company.number.blankToNull(),
                bairro = company.neighborhood.blankToNull(),
                cidade = company.city.blankToNull(),
                estado = company.state.blankToNull(),
                restoIngesta = company.leftoverMeals,
                linha = company.line,
                qtdbutao = company.buttonCount,
                tempoAutoEnvioCurto = company.shortAutoSendTime,
                tempoAutoEnvioLongo = company.longAutoSendTime,
                comentarioTodosEmoticons = company.commentAllRatings,
                tipo = company.type.name,
                adminOthersPercent = company.adminOthersPercent,
            )
        if (isUuid(company.id)) {
            patch<CompanyDto>("/companies/${company.id}", request)
        } else {
            post<CompanyDto>("/companies", request)
        }
        invalidateCompanies()
    }

    override suspend fun loadServices(companyId: String): List<CompanyService> {
        val page = get<CompanyPageDto<CompanyServiceDto>>("/company-services?companyId=$companyId&status=true&page=0&size=100")
        return page.content.map(CompanyServiceDto::toDomain)
    }

    override suspend fun resolvePublicMonitor(code: String): PublicMonitor = get<PublicMonitorDto>("/companies/public-monitor/$code").toDomain()

    override suspend fun loadPublicMonitorAnalytics(companyId: String): PublicMonitorAnalytics =
        httpClient
            .get("$SATISFACTION_API_URL/public-monitor/$companyId/analytics")
            .let { response ->
                if (!response.status.isSuccess()) error("HTTP ${response.status.value}")
                response.body<PublicMonitorAnalyticsDto>().toDomain()
            }

    override suspend fun loadServiceTypes(): List<ServiceTypeOption> =
        get<CompanyPageDto<ServiceTypeDto>>("/service-types?status=true&page=0&size=100").content.map {
            ServiceTypeOption(it.id, it.nome)
        }

    override suspend fun loadLines(): List<Line> = get<List<LineDto>>("/lines").map(LineDto::toDomain)

    override suspend fun createLine(
        code: String,
        name: String,
        displayOrder: Int,
        isDefault: Boolean,
    ): Line =
        post<LineDto>(
            "/lines",
            LineRequestDto(code, name, displayOrder, isDefault),
        ).toDomain()

    override suspend fun updateLine(line: Line): Line =
        patch<LineDto>(
            "/lines/${line.id}",
            LineUpdateDto(line.code, line.name, line.displayOrder, line.isDefault, line.active),
        ).toDomain()

    override suspend fun deactivateLine(id: String) {
        val response = httpClient.delete(baseUrl + "/lines/$id") { authorize() }
        if (!response.status.isSuccess()) error("HTTP ${response.status.value}")
    }

    override suspend fun syncMissingFromSatisfaction(): CompanyMissingSyncResult =
        request<CompanyMissingSyncResultDto> {
            httpClient.post(baseUrl + "/companies/sync-missing") { authorize() }
        }.let(CompanyMissingSyncResultDto::toDomain)

    override suspend fun loadEmotionCatalog(companyId: String): List<CompanyEmotion> = get<List<EmotionDto>>("/companies/$companyId/emotions/catalog").map(EmotionDto::toDomain)

    override suspend fun loadCompanyEmotions(companyId: String): List<CompanyEmotion> = get<EmotionContainerDto>("/companies/$companyId/emotions").emotions.map(EmotionDto::toDomain)

    override suspend fun updateCompanyEmotions(
        companyId: String,
        emotionIds: List<String>,
    ): List<CompanyEmotion> = put<EmotionContainerDto>("/companies/$companyId/emotions", EmotionIdsRequestDto(emotionIds)).emotions.map(EmotionDto::toDomain)

    override suspend fun addEmotionResponse(
        companyId: String,
        emotionId: String,
        text: String,
    ): CompanyEmotionResponse = post<EmotionResponseDto>("/companies/$companyId/emotions/responses", EmotionResponseRequestDto(emotionId, text)).toDomain()

    override suspend fun updateEmotionResponse(
        responseId: String,
        text: String,
    ): CompanyEmotionResponse = patch<EmotionResponseDto>("/companies/emotions/responses/$responseId", EmotionTextRequestDto(text)).toDomain()

    override suspend fun deleteEmotionResponse(responseId: String) {
        val response = httpClient.delete(baseUrl + "/companies/emotions/responses/$responseId") { authorize() }
        if (!response.status.isSuccess()) error("HTTP ${response.status.value}")
    }

    override suspend fun createService(
        companyId: String,
        draft: CompanyServiceDraft,
    ): CompanyService = post<CompanyServiceDto>("/company-services?companyId=$companyId", draft.toRequest()).toDomain()

    override suspend fun updateService(
        serviceId: String,
        draft: CompanyServiceDraft,
    ): CompanyService = patch<CompanyServiceDto>("/company-services/$serviceId", draft.toRequest()).toDomain()

    override suspend fun deleteService(serviceId: String) {
        val response =
            httpClient.delete(baseUrl + "/company-services/$serviceId") {
                authorize()
            }
        if (!response.status.isSuccess()) {
            error("HTTP ${response.status.value}")
        }
    }

    override suspend fun loadCostCenters(companyId: String): List<CostCenter> = get<CompanyPageDto<CostCenterDto>>("/cost-centers?companyId=$companyId&page=0&size=100").content.map(CostCenterDto::toDomain)

    override suspend fun loadAllCostCenters(): List<CostCenter> {
        val firstPage = get<CompanyPageDto<CostCenterDto>>("/cost-centers?page=0&size=100")
        val extraPages =
            (1 until firstPage.page.totalPages).parallelMap { page ->
                get<CompanyPageDto<CostCenterDto>>("/cost-centers?page=$page&size=100").content
            }
        return (firstPage.content + extraPages.flatten()).map(CostCenterDto::toDomain)
    }

    override suspend fun createCostCenter(
        companyId: String,
        name: String,
        code: String,
    ): CostCenter = post<CostCenterDto>("/cost-centers?companyId=$companyId", CostCenterRequestDto(name, code.blankToNull())).toDomain()

    override suspend fun updateCostCenter(
        id: String,
        name: String,
        code: String,
    ): CostCenter = patch<CostCenterDto>("/cost-centers/$id", CostCenterRequestDto(name, code.blankToNull())).toDomain()

    override suspend fun changeCostCenterStatus(
        center: CostCenter,
        active: Boolean,
    ): CostCenter = patch<CostCenterDto>("/cost-centers/${center.id}/status", CostCenterStatusDto(active)).toDomain()

    override suspend fun deleteCostCenter(id: String) {
        val response = httpClient.delete(baseUrl + "/cost-centers/$id") { authorize() }
        if (!response.status.isSuccess()) error("HTTP ${response.status.value}")
    }

    override suspend fun changeStatus(
        company: Company,
        active: Boolean,
    ) {
        patch<CompanyDto>("/companies/${company.id}/status", CompanyStatusDto(status = active))
        invalidateCompanies()
    }

    private suspend inline fun <reified T> get(path: String): T =
        request {
            httpClient.get(baseUrl + path) {
                authorize()
            }
        }

    private suspend inline fun <reified T> post(
        path: String,
        body: Any,
    ): T = request { httpClient.post(baseUrl + path) { jsonBody(body) } }

    private suspend inline fun <reified T> patch(
        path: String,
        body: Any,
    ): T = request { httpClient.patch(baseUrl + path) { jsonBody(body) } }

    private suspend inline fun <reified T> put(
        path: String,
        body: Any,
    ): T = request { httpClient.put(baseUrl + path) { jsonBody(body) } }

    private suspend inline fun <reified T> request(crossinline call: suspend () -> io.ktor.client.statement.HttpResponse): T {
        val response = call()
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<CompanyApiErrorDto>(response.bodyAsText())
                        .error
                        ?.message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response.body()
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private fun io.ktor.client.request.HttpRequestBuilder.jsonBody(body: Any) {
        authorize()
        contentType(ContentType.Application.Json)
        setBody(body)
    }

    private fun isUuid(value: String): Boolean = value.matches(UUID_PATTERN)

    private companion object {
        val UUID_PATTERN = Regex("[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}")
    }
}

@Serializable
private data class CompanyPageDto<T>(
    val content: List<T> = emptyList(),
    val page: CompanyPageMetadata = CompanyPageMetadata(),
)

@Serializable
private data class CompanyPageMetadata(
    val totalPages: Int = 1,
)

@Serializable
private data class CostCenterDto(
    val id: String,
    val companyId: String,
    val nome: String = "",
    val codigo: String? = null,
    val origemCadastro: String = "MANUAL",
    val ativo: Boolean = true,
) {
    fun toDomain() = CostCenter(id, companyId, nome, codigo.orEmpty(), origemCadastro, ativo)
}

@Serializable
private data class CostCenterRequestDto(
    val nome: String,
    val codigo: String? = null,
)

@Serializable
private data class CostCenterStatusDto(
    val ativo: Boolean,
)

@Serializable
private data class CompanyDto(
    val id: String,
    val nome: String = "",
    val razaoSocial: String = "",
    val logo: String? = null,
    val email: String? = null,
    val login: String? = null,
    val telcom: String? = null,
    val telcel: String? = null,
    val cnpj: String? = null,
    val qtFuncionarios: Int? = null,
    val cep: String? = null,
    val rua: String? = null,
    val numero: String? = null,
    val bairro: String? = null,
    val cidade: String? = null,
    val estado: String? = null,
    val restoIngesta: Int? = null,
    val status: Boolean = true,
    val userEdt: String? = null,
    val updatedAt: String? = null,
    val linha: String? = null,
    val qtdbutao: Int? = null,
    val tempoAutoEnvioCurto: Int? = null,
    val tempoAutoEnvioLongo: Int? = null,
    val comentarioTodosEmoticons: Boolean = false,
    val tipo: String? = null,
    val adminOthersPercent: Double? = null,
)

@Serializable
private data class CompanyServiceDto(
    val id: String,
    val companyId: String = "",
    val tipoServico: String? = null,
    val nome: String = "",
    val horaInicio: String? = null,
    val horaFinal: String? = null,
    val preco: Double = 0.0,
    val status: Boolean = true,
    val qtdRefDiaria: Int? = null,
    val qtdRef: Int? = null,
    val qtdRefSegunda: Int? = null,
    val qtdRefTerca: Int? = null,
    val qtdRefQuarta: Int? = null,
    val qtdRefQuinta: Int? = null,
    val qtdRefSexta: Int? = null,
    val qtdRefSabado: Int? = null,
    val qtdRefDomingo: Int? = null,
    val domingoAtivo: Boolean = false,
    val qtdRefDiasAtivos: Map<String, List<Int>> = emptyMap(),
    val lines: List<LineDto> = emptyList(),
)

@Serializable
private data class LineDto(
    val id: String,
    val code: String = "",
    val name: String = "",
    val active: Boolean = true,
    val displayOrder: Int = 0,
    val isDefault: Boolean = false,
)

@Serializable
private data class LineRequestDto(
    val code: String,
    val name: String,
    val displayOrder: Int,
    val isDefault: Boolean,
)

@Serializable
private data class LineUpdateDto(
    val code: String,
    val name: String,
    val displayOrder: Int,
    val isDefault: Boolean,
    val active: Boolean,
)

@Serializable
private data class CompanyMissingSyncResultDto(
    val companiesCreated: Int = 0,
    val companiesAlreadyExisting: Int = 0,
    val servicesCreated: Int = 0,
    val servicesAlreadyExisting: Int = 0,
    val linesCreated: Int = 0,
    val associationsCreated: Int = 0,
)

@Serializable
private data class PublicMonitorDto(
    val companyId: String,
    val companyName: String,
    val active: Boolean = true,
    val buttonCount: Int = 4,
    val services: List<PublicMonitorServiceDto> = emptyList(),
)

@Serializable
private data class PublicMonitorServiceDto(
    val name: String = "",
    val startTime: String = "",
    val endTime: String = "",
)

@Serializable
private data class PublicMonitorAnalyticsDto(
    val totalVotes: Int = 0,
    val avaliacoesPorTipo: Map<String, Int> = emptyMap(),
    val votesByService: Map<String, PublicMonitorServiceAnalyticsDto> = emptyMap(),
)

@Serializable
private data class PublicMonitorServiceAnalyticsDto(
    val total: Int = 0,
    val avaliacoes: Map<String, Int> = emptyMap(),
    val serviceInfo: PublicMonitorServiceInfoDto? = null,
)

@Serializable
private data class PublicMonitorServiceInfoDto(
    @SerialName("nome") val name: String = "",
    @SerialName("hora_inicio") val startTime: String = "",
    @SerialName("hora_final") val endTime: String = "",
)

@Serializable
private data class ServiceTypeDto(
    val id: String,
    val nome: String = "",
)

@Serializable
private data class CompanyServiceRequestDto(
    val tipoServico: String,
    val nome: String,
    val horaInicio: String,
    val horaFinal: String,
    val preco: Double,
    val qtdRef: Int,
    val qtdRefSegunda: Int,
    val qtdRefTerca: Int,
    val qtdRefQuarta: Int,
    val qtdRefQuinta: Int,
    val qtdRefSexta: Int,
    val qtdRefSabado: Int,
    val qtdRefDomingo: Int,
    val domingoAtivo: Boolean,
    val qtdRefDiaria: Int,
    val qtdRefDiasAtivos: Map<String, List<Int>>,
    val lineIds: List<String>,
)

@Serializable
private data class CompanyRequestDto(
    val nome: String,
    val razaoSocial: String,
    val logo: String? = null,
    val email: String? = null,
    val login: String? = null,
    val password: String? = null,
    val telcom: String? = null,
    val telcel: String? = null,
    val cnpj: String? = null,
    val qtFuncionarios: Int? = null,
    val cep: String? = null,
    val rua: String? = null,
    val numero: String? = null,
    val bairro: String? = null,
    val cidade: String? = null,
    val estado: String? = null,
    val restoIngesta: Int? = null,
    val linha: String? = null,
    val qtdbutao: Int? = null,
    val tempoAutoEnvioCurto: Int? = null,
    val tempoAutoEnvioLongo: Int? = null,
    val comentarioTodosEmoticons: Boolean = false,
    val tipo: String? = null,
    val adminOthersPercent: Double? = null,
)

@Serializable
private data class CompanyStatusDto(
    val status: Boolean,
)

@Serializable
private data class CompanyApiErrorDto(
    val error: CompanyApiErrorMessageDto? = null,
)

@Serializable
private data class CompanyApiErrorMessageDto(
    val message: String? = null,
)

@Serializable
private data class EmotionContainerDto(
    val emotions: List<EmotionDto> = emptyList(),
)

@Serializable
private data class EmotionDto(
    val id: String,
    val emoji: String = "",
    val nome: String = "",
    val respostas: List<EmotionResponseDto> = emptyList(),
) {
    fun toDomain() = CompanyEmotion(id, emoji, nome, respostas.map(EmotionResponseDto::toDomain))
}

@Serializable
private data class EmotionResponseDto(
    val id: String? = null,
    val texto: String = "",
    val custom: Boolean = false,
) {
    fun toDomain() = CompanyEmotionResponse(id, texto, custom)
}

@Serializable
private data class EmotionIdsRequestDto(
    @SerialName("emotion_ids") val emotionIds: List<String>,
)

@Serializable
private data class EmotionResponseRequestDto(
    @SerialName("emotion_id") val emotionId: String,
    val texto: String,
)

@Serializable
private data class EmotionTextRequestDto(
    val texto: String,
)

private fun CompanyDto.toDomain(): Company =
    Company(
        id = id,
        name = nome,
        legalName = razaoSocial,
        type = tipo.toCompanyType(),
        logo = logo.orEmpty(),
        email = email.orEmpty(),
        login = login.orEmpty(),
        commercialPhone = telcom.orEmpty(),
        mobilePhone = telcel.orEmpty(),
        cnpj = cnpj.orEmpty(),
        employeeCount = qtFuncionarios ?: 0,
        cep = cep.orEmpty(),
        street = rua.orEmpty(),
        number = numero.orEmpty(),
        neighborhood = bairro.orEmpty(),
        city = cidade.orEmpty(),
        state = estado.orEmpty(),
        leftoverMeals = restoIngesta ?: 0,
        line = linha ?: "0",
        buttonCount = qtdbutao ?: 3,
        shortAutoSendTime = tempoAutoEnvioCurto ?: 2,
        longAutoSendTime = tempoAutoEnvioLongo ?: 45,
        commentAllRatings = comentarioTodosEmoticons,
        directorNames = emptyList(),
        managerNames = emptyList(),
        updatedBy = userEdt.orEmpty(),
        updatedAt = updatedAt.orEmpty(),
        inactive = !status,
        adminOthersPercent = adminOthersPercent ?: DEFAULT_ADMIN_OTHERS_PERCENT,
    )

private fun CompanyServiceDto.toDomain(): CompanyService =
    CompanyService(
        id = id,
        companyId = companyId,
        serviceType = tipoServico.orEmpty(),
        name = nome,
        startTime = horaInicio.orEmpty(),
        endTime = horaFinal.orEmpty(),
        price = preco,
        active = status,
        dailyQuantity = qtdRefDiaria ?: 0,
        quantity = qtdRef ?: 0,
        mondayQuantity = qtdRefSegunda ?: 0,
        tuesdayQuantity = qtdRefTerca ?: 0,
        wednesdayQuantity = qtdRefQuarta ?: 0,
        thursdayQuantity = qtdRefQuinta ?: 0,
        fridayQuantity = qtdRefSexta ?: 0,
        saturdayQuantity = qtdRefSabado ?: 0,
        sundayQuantity = qtdRefDomingo ?: 0,
        sundayActive = domingoAtivo,
        activeDays = qtdRefDiasAtivos,
        lines = lines.map(LineDto::toDomain),
    )

private fun LineDto.toDomain() = Line(id, code, name, active, displayOrder, isDefault)

private fun CompanyMissingSyncResultDto.toDomain() =
    CompanyMissingSyncResult(
        companiesCreated,
        companiesAlreadyExisting,
        servicesCreated,
        servicesAlreadyExisting,
        linesCreated,
        associationsCreated,
    )

private fun PublicMonitorDto.toDomain() =
    PublicMonitor(
        companyId = companyId,
        companyName = companyName,
        active = active,
        buttonCount = buttonCount,
        services = services.map { PublicMonitorService(it.name, it.startTime, it.endTime) },
    )

private fun PublicMonitorAnalyticsDto.toDomain() =
    PublicMonitorAnalytics(
        totalVotes = totalVotes,
        ratingsByType = avaliacoesPorTipo,
        totalVotesByService =
            votesByService
                .mapNotNull { (_, service) ->
                    service.serviceInfo?.name?.let { it to service.total }
                }.toMap(),
        ratingsByService =
            votesByService
                .mapNotNull { (_, service) ->
                    service.serviceInfo?.name?.let { it to service.avaliacoes }
                }.toMap(),
        services =
            votesByService.values.mapNotNull { service ->
                service.serviceInfo?.let { PublicMonitorService(it.name, it.startTime, it.endTime) }
            },
    )

private const val SATISFACTION_API_URL = "https://pesquisa.api.vvrefeicoes.com.br"

private fun CompanyServiceDraft.toRequest() =
    CompanyServiceRequestDto(
        tipoServico = serviceType,
        nome = name,
        horaInicio = startTime,
        horaFinal = endTime,
        preco = price.replace(',', '.').toDoubleOrNull() ?: 0.0,
        qtdRef = quantity,
        qtdRefSegunda = mondayQuantity,
        qtdRefTerca = tuesdayQuantity,
        qtdRefQuarta = wednesdayQuantity,
        qtdRefQuinta = thursdayQuantity,
        qtdRefSexta = fridayQuantity,
        qtdRefSabado = saturdayQuantity,
        qtdRefDomingo = sundayQuantity,
        domingoAtivo = sundayActive,
        qtdRefDiaria = dailyQuantity,
        qtdRefDiasAtivos = activeDays,
        lineIds = lineIds,
    )

private fun String?.toCompanyType(): CompanyType =
    when (this?.uppercase()) {
        CompanyType.TRA.name -> CompanyType.TRA
        CompanyType.LOJ.name -> CompanyType.LOJ
        CompanyType.EVENTOS.name -> CompanyType.EVENTOS
        CompanyType.UPR.name -> CompanyType.UPR
        else -> CompanyType.UPR
    }

private fun String.blankToNull(): String? = trim().takeIf(String::isNotEmpty)
